import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Seo from '../components/Seo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_COLORS = {
  SVA: { bg: 'bg-[#E8DDD3]', text: 'text-[#8B6E4E]', accent: '#8B6E4E' },
  SAMASHTI: { bg: 'bg-[#D6E4EC]', text: 'text-[#3A6B8C]', accent: '#3A6B8C' },
  SHODH: { bg: 'bg-[#DDE8D6]', text: 'text-[#5A7A4A]', accent: '#5A7A4A' },
};

const getCategoryStyle = (category) => CATEGORY_COLORS[category] || CATEGORY_COLORS.SVA;

const Work = () => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [activeCategory, setActiveCategory] = useState('SANGAM');
  const [activeDiscipline, setActiveDiscipline] = useState('all');
  const [loading, setLoading] = useState(true);

  const categories = [
    { key: 'SANGAM', label: 'sangam', description: 'all projects' },
    { key: 'SVA', label: 'sva', description: 'private / residential' },
    { key: 'SAMASHTI', label: 'samashti', description: 'public / institutional' },
    { key: 'SHODH', label: 'shodh', description: 'experimental / research' }
  ];

  const disciplines = ['all', 'architecture', 'interior', 'landscape'];

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    filterProjects();
  }, [activeCategory, activeDiscipline, projects]);

  const loadProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to load projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterProjects = () => {
    let filtered = [...projects];
    if (activeCategory !== 'SANGAM') {
      filtered = filtered.filter(p => p.category === activeCategory);
    }
    if (activeDiscipline !== 'all') {
      filtered = filtered.filter(p => (p.discipline || '').toLowerCase() === activeDiscipline);
    }
    setFilteredProjects(filtered);
  };

  return (
    <div className="min-h-screen bg-white pt-24" data-testid="work-page">
      <Seo
        title="work — studio 2+2, landscape architecture practice, Ahmedabad"
        description="84 selected projects — gardens, farmhouses, public landscapes, campuses and homes across India by studio 2+2."
      />
      {/* Filters */}
      <section className="sticky top-16 z-40 bg-white border-b border-gray-100">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="flex space-x-8 overflow-x-auto py-6 border-b border-gray-100" data-testid="category-filters">
            {categories.map((cat) => (
              <button
                key={cat.key}
                data-testid={`filter-${cat.key.toLowerCase()}`}
                onClick={() => setActiveCategory(cat.key)}
                className={`flex-shrink-0 pb-2 border-b-2 transition-colors duration-300 ${
                  activeCategory === cat.key
                    ? 'border-[#002F6C] text-[#002F6C]'
                    : 'border-transparent text-gray-500 hover:text-gray-900'
                }`}
                style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700 }}
              >
                <div className="text-center lowercase">
                  <div className="text-sm tracking-wide mb-1">{cat.label}</div>
                  <div className="text-xs text-gray-500" style={{ fontWeight: 300 }}>{cat.description}</div>
                </div>
              </button>
            ))}
          </div>

          <div className="flex space-x-6 py-4" data-testid="discipline-filters">
            {disciplines.map((disc) => (
              <button
                key={disc}
                data-testid={`filter-disc-${disc}`}
                onClick={() => setActiveDiscipline(disc)}
                className={`text-xs tracking-wide transition-colors duration-300 lowercase ${
                  activeDiscipline === disc
                    ? 'text-[#002F6C]'
                    : 'text-gray-500 hover:text-gray-900'
                }`}
                style={{ fontFamily: 'Inter, sans-serif', fontWeight: activeDiscipline === disc ? 700 : 300 }}
              >
                {disc}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-8 px-4 md:px-8 lg:px-12">
        <div className="w-full">
          {loading ? (
            <div className="text-center py-20" data-testid="loading-spinner">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#002F6C] mx-auto"></div>
              <p className="mt-4 text-gray-600" style={{ fontFamily: 'Lato, sans-serif', fontWeight: 300 }}>loading projects...</p>
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="text-center py-20" data-testid="no-projects">
              <p className="text-gray-600 text-lg lowercase" style={{ fontFamily: 'Lato, sans-serif', fontWeight: 300 }}>no projects found for this selection.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1" data-testid="projects-grid">
              {filteredProjects.map((project) => {
                const catStyle = getCategoryStyle(project.category);
                const hasImage = !!project.featured_image;

                return (
                  <Link
                    key={project.id}
                    to={`/work/${project.id}`}
                    className="group relative bg-gray-200 overflow-hidden cursor-pointer block"
                    data-testid={`project-card-${project.id}`}
                  >
                    {/* Image / Color Placeholder */}
                    <div className="w-full aspect-[4/3]">
                      {hasImage ? (
                        <img
                          src={project.featured_image}
                          alt={[project.title, project.typology, project.location].filter(Boolean).join(', ')}
                          loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        />
                      ) : (
                        <div className={`w-full h-full ${catStyle.bg} flex flex-col items-center justify-center p-6 group-hover:scale-105 transition-transform duration-700`}>
                          <span
                            className={`text-3xl font-bold uppercase tracking-widest opacity-20 ${catStyle.text}`}
                            style={{ fontFamily: 'Inter, sans-serif' }}
                          >
                            {project.category}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Project Info - Always visible */}
                    <div className="p-4 bg-white border-t border-gray-100">
                      <h3
                        className="text-sm text-gray-900 lowercase mb-1 group-hover:text-[#002F6C] transition-colors"
                        style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, letterSpacing: '0.03em' }}
                        data-testid={`project-title-${project.id}`}
                      >
                        {project.title}
                      </h3>
                      {project.subtitle && (
                        <p
                          className="text-xs text-gray-500 lowercase mb-2"
                          style={{ fontFamily: 'Lato, sans-serif', fontWeight: 300 }}
                        >
                          {project.subtitle}
                        </p>
                      )}
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className="text-[10px] uppercase tracking-wider font-bold"
                          style={{ fontFamily: 'Inter, sans-serif', color: catStyle.accent }}
                        >
                          {project.category}
                        </span>
                        {project.discipline && (
                          <>
                            <span className="text-gray-300 text-[10px]">|</span>
                            <span className="text-[10px] text-gray-500 lowercase" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 500 }}>
                              {project.discipline}
                            </span>
                          </>
                        )}
                        {project.location && (
                          <>
                            <span className="text-gray-300 text-[10px]">|</span>
                            <span className="text-[10px] text-gray-500 lowercase" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 500 }}>
                              {project.location}
                            </span>
                          </>
                        )}
                        {project.year && (
                          <>
                            <span className="text-gray-300 text-[10px]">|</span>
                            <span className="text-[10px] text-gray-500" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 500 }}>
                              {project.year}
                            </span>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Hover overlay on image area only */}
                    <div className="absolute top-0 left-0 right-0 aspect-[4/3] bg-[#002F6C] bg-opacity-0 group-hover:bg-opacity-60 transition-all duration-500 flex items-center justify-center pointer-events-none">
                      <span
                        className="text-white text-xs uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                        style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}
                      >
                        view project
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Work;
